import React, { useState, useEffect } from 'react';
import BottomNavigation from './BottomNavigation';
import {
  getUserSubscribedTopics,
  subscribeToTopics,
  unsubscribeFromTopics,
  getAvailableTopics,
  requestNotificationPermission
} from '../services/fcmService';

const NotificationSettings = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribedTopics, setSubscribedTopics] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedEducation, setSelectedEducation] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const availableTopics = getAvailableTopics();

  useEffect(() => {
    if (user) {
      loadSubscribedTopics();
      checkNotificationPermission();
    }
  }, [user]);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const loadSubscribedTopics = async () => {
    setLoading(true);
    try {
      const result = await getUserSubscribedTopics(user.uid);
      if (result.success) {
        setSubscribedTopics(result.topics);

        // Parse topics into industries and education
        const industries = [];
        const education = [];

        result.topics.forEach(topic => {
          if (topic.startsWith('industry_')) {
            industries.push(topic.replace('industry_', '').replace(/_/g, ' '));
          } else if (topic.startsWith('education_')) {
            education.push(topic.replace('education_', '').replace(/_/g, ' '));
          }
        });

        setSelectedIndustries(industries);
        setSelectedEducation(education);
      }
    } catch (error) {
      console.error('Error loading subscribed topics:', error);
      showMessage('error', 'Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const result = await requestNotificationPermission(user.uid);
      if (result.success) {
        setNotificationsEnabled(true);
        showMessage('success', 'Notifications enabled successfully!');
      } else {
        showMessage('error', 'Failed to enable notifications. Please check browser permissions.');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      showMessage('error', 'Failed to enable notifications');
    }
  };

  const handleIndustryToggle = (industry) => {
    setSelectedIndustries(prev => {
      if (prev.includes(industry)) {
        return prev.filter(i => i !== industry);
      } else {
        return [...prev, industry];
      }
    });
  };

  const handleEducationToggle = (education) => {
    setSelectedEducation(prev => {
      if (prev.includes(education)) {
        return prev.filter(e => e !== education);
      } else {
        return [...prev, education];
      }
    });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Generate topic names
      const industryTopics = selectedIndustries.map(ind => `industry_${ind.replace(/\s+/g, '_')}`);
      const educationTopics = selectedEducation.map(edu => `education_${edu.replace(/\s+/g, '_')}`);
      const newTopics = [...industryTopics, ...educationTopics];

      // Find topics to unsubscribe
      const topicsToUnsubscribe = subscribedTopics.filter(topic => !newTopics.includes(topic));

      // Find topics to subscribe
      const topicsToSubscribe = newTopics.filter(topic => !subscribedTopics.includes(topic));

      // Unsubscribe from removed topics
      if (topicsToUnsubscribe.length > 0) {
        await unsubscribeFromTopics(user.uid, topicsToUnsubscribe);
      }

      // Subscribe to new topics
      if (topicsToSubscribe.length > 0) {
        await subscribeToTopics(user.uid, topicsToSubscribe);
      }

      setSubscribedTopics(newTopics);
      showMessage('success', 'Notification settings saved successfully!');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showMessage('error', 'Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-20 lg:pb-0">
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
              <p className="text-gray-600 mt-1">Manage your job notification preferences</p>
            </div>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl ${message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className="font-medium">{message.text}</span>
            </div>
          </div>
        )}

        {/* Enable Notifications */}
        {!notificationsEnabled && (
          <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Enable Browser Notifications</h3>
                <p className="text-yellow-800 mb-4">
                  To receive job notifications, you need to enable browser notifications first.
                </p>
                <button
                  onClick={handleEnableNotifications}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                >
                  Enable Notifications
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Industry Preferences */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Industry Preferences
          </h3>
          <p className="text-gray-600 mb-4">Select industries you're interested in to receive relevant job notifications</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {availableTopics.industries.map((industry) => (
              <label
                key={industry}
                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedIndustries.includes(industry)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIndustries.includes(industry)}
                  onChange={() => handleIndustryToggle(industry)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className={`ml-3 text-sm font-medium ${selectedIndustries.includes(industry) ? 'text-blue-900' : 'text-gray-700'
                  }`}>
                  {industry}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Education Preferences */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
            </svg>
            Education Requirements
          </h3>
          <p className="text-gray-600 mb-4">Select education levels to get notified about matching jobs</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {availableTopics.educationRequirements.map((education) => (
              <label
                key={education}
                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedEducation.includes(education)
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
              >
                <input
                  type="checkbox"
                  checked={selectedEducation.includes(education)}
                  onChange={() => handleEducationToggle(education)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className={`ml-3 text-sm font-medium ${selectedEducation.includes(education) ? 'text-green-900' : 'text-gray-700'
                  }`}>
                  {education}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-2">Your Notification Preferences</h4>
          <div className="flex flex-wrap gap-2">
            {selectedIndustries.length === 0 && selectedEducation.length === 0 ? (
              <span className="text-gray-500 text-sm">No preferences selected yet</span>
            ) : (
              <>
                {selectedIndustries.map(industry => (
                  <span key={industry} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {industry}
                  </span>
                ))}
                {selectedEducation.map(education => (
                  <span key={education} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    {education}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={loadSubscribedTopics}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
          >
            Reset
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};

export default NotificationSettings;
